import { FC, useEffect } from "react";
import { $getSelection, $insertNodes, $isRangeSelection, COMMAND_PRIORITY_HIGH, createEditor, LexicalEditor, PASTE_COMMAND } from "lexical";
import { $convertFromMarkdownString, BOLD_STAR, BOLD_UNDERSCORE, ITALIC_STAR, ITALIC_UNDERSCORE, LINK, ORDERED_LIST, UNORDERED_LIST } from '@lexical/markdown';
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { ListItemNode, ListNode } from "@lexical/list";
import { AutoLinkNode, LinkNode } from "@lexical/link";

const SUPPORTED_MARKDOWN = [
  UNORDERED_LIST,
  ORDERED_LIST,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK
];

export const MarkdownPastePlugin: FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        if (!text) return false;

        event.preventDefault();
        convertMarkdownToDOM(text)
          .then(dom => {
            editor.update(() => pasteDOMIntoEditor(dom, editor, text));
          })
          .catch(() => handlePasteFallback(text));
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);
  return null;
}

const pasteDOMIntoEditor = (dom: Document, editor: LexicalEditor, fallback: string) => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  if (!selection.isCollapsed()) {
    selection.removeText();
  }

  const nodes = $generateNodesFromDOM(editor, dom);

  if (nodes.length === 0) {
    selection.insertText(fallback);
  } else {
    $insertNodes(nodes);
  }
}

const handlePasteFallback = (text: string) => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  if (!selection.isCollapsed()) {
    selection.removeText();
  }
  
  selection.insertText(text);
}

const markdownParser = createEditor({
  namespace: 'MarkdownPasteParser',
  nodes: [ListNode, ListItemNode, LinkNode, AutoLinkNode],
});
const convertMarkdownToDOM = (text: string): Promise<Document> => {

  return new Promise((resolve, reject) => {
    markdownParser.update(() => {
      // this shouldn't throw any errors, but just in case something goes wrong
      // this way we can let regular pasting still run its course
      try {
        $convertFromMarkdownString(text, SUPPORTED_MARKDOWN);
        
        const html = $generateHtmlFromNodes(markdownParser);
        if (!html) {
          reject();
          return;
        }

        const dom = new DOMParser().parseFromString(html, 'text/html');
        if (!dom) {
          reject();
          return;
        }

        resolve(dom);
      } catch (e) {
        reject()
      }
    });
  });
}
